import { useEffect, useState, type ReactNode } from 'react'
import { useParams } from 'react-router-dom'
import { FileText, Download } from 'lucide-react'
import { api, PublicShareInfo } from '@/services/api'
import { Button } from '@/components/ui/button'
import { LogoMark } from '@/components/Logo'

function Shell({ children }: { children: ReactNode }) {
  return (
    <div className="flex min-h-screen flex-col items-center justify-center bg-background p-6">
      <div className="w-full max-w-md animate-fade-in-up">
        <div className="mb-6 flex items-center justify-center gap-2">
          <LogoMark size={24} />
          <span className="font-semibold tracking-tight">Freehold</span>
        </div>
        {children}
      </div>
    </div>
  )
}

export function PublicSharePage() {
  const { token } = useParams<{ token: string }>()
  const [info, setInfo] = useState<PublicShareInfo | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    let mounted = true
    const load = async () => {
      if (!token) {
        setError('Invalid share link')
        setLoading(false)
        return
      }
      try {
        const data = await api.publicShare.info(token)
        if (!mounted) return
        setInfo(data)
      } catch (e) {
        if (!mounted) return
        setError((e as Error)?.message || 'This public share is unavailable')
      } finally {
        if (mounted) setLoading(false)
      }
    }
    load()
    return () => {
      mounted = false
    }
  }, [token])

  if (loading) {
    return (
      <Shell>
        <div className="rounded-xl border bg-card p-8 text-center shadow-sm">
          <div className="mx-auto mb-3 h-5 w-5 animate-spin rounded-full border-2 border-muted border-t-primary" aria-label="Loading" />
          <p className="text-sm text-muted-foreground">Loading shared file…</p>
        </div>
      </Shell>
    )
  }

  if (error || !info || !token) {
    return (
      <Shell>
        <div className="rounded-xl border bg-card p-8 text-center shadow-sm">
          <h1 className="mb-1 text-lg font-semibold">File unavailable</h1>
          <p className="text-sm text-muted-foreground">{error || 'This link is no longer available.'}</p>
        </div>
      </Shell>
    )
  }

  return (
    <Shell>
      <div className="overflow-hidden rounded-xl border bg-card shadow-sm">
        <div className="flex flex-col items-center gap-4 p-7 text-center">
          <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-primary/10 text-primary">
            <FileText className="h-7 w-7" />
          </div>
          <div>
            <h1 className="text-lg font-semibold leading-tight">{info.title}</h1>
            <p className="mt-1 text-sm text-muted-foreground">A file has been shared with you</p>
          </div>
          <div className="w-full rounded-lg border bg-muted/40 px-4 py-3 text-left">
            <p className="text-xs text-muted-foreground">File</p>
            <p className="break-all font-medium">{info.file_name}</p>
            {info.label && <p className="mt-1 text-sm text-muted-foreground">{info.label}</p>}
          </div>
          <Button asChild className="w-full">
            <a href={api.publicShare.downloadUrl(token)}>
              <Download className="mr-2 h-4 w-4" />
              Download file
            </a>
          </Button>
        </div>
      </div>
      <p className="mt-4 text-center text-xs text-muted-foreground">Shared securely via Freehold</p>
    </Shell>
  )
}
